import { EvaluatorToEvaluateeRole } from './evaluator-impact.types';

export type Evaluator = {
    profile: {
        givenName: string;
        familyName: string;
    };
    userSub: string;
};

export enum EvaluationStatus {
    REQUESTED = 'REQUESTED',
    SUBMITTED = 'SUBMITTED',
    UPDATE_REQUESTED = 'UPDATE_REQUESTED',
    UPDATE_APPROVED = 'UPDATE_APPROVED',
}

export type PerformEvaluationScore = {
    topic: { name: string; id: string };
    score: EvaluationScoreLabel | null;
    note?: string;
};

export enum EvaluationScoreValue {
    NEGATIVE = 0.1,
    NEUTRAL = 0.2,
    POSITIVE = 0.3,
}

export enum EvaluationScoreLabel {
    NEGATIVE = 'NEGATIVE',
    NEUTRAL = 'NEUTRAL',
    POSITIVE = 'POSITIVE',
}

export const EvaluationScoreMap: Record<EvaluationScoreLabel, EvaluationScoreValue> = {
    [EvaluationScoreLabel.NEGATIVE]: EvaluationScoreValue.NEGATIVE,
    [EvaluationScoreLabel.NEUTRAL]: EvaluationScoreValue.NEUTRAL,
    [EvaluationScoreLabel.POSITIVE]: EvaluationScoreValue.POSITIVE,
};

export type EvaluatorScore = {
    topic: string;
    score: EvaluationScoreLabel;
    note?: string;
};

export type TopicScore = {
    evaluationId: string;
    topicId: string;
    topic: {
        name: string;
        id: string;
        description: string;
        neutralScoreDescription: string;
        positiveScoreDescription: string;
        negativeScoreDescription: string;
        topicCategoryId: string;
    };
    score: EvaluationScoreLabel;
    note?: string;
};

export type Evaluation = {
    id: string;
    evaluatorUserSub: string;
    evaluateeUserSub: string;
    dueDate: Date;
    evaluatorToEvaluateeRoleId: string;
    status: EvaluationStatus;
    evaluator?: {
        profile: {
            givenName: string;
            familyName: string;
        };
        userSub?: string;
    };
    evaluatee?: {
        profile: {
            givenName: string;
            familyName: string;
        };
        userSub?: string;
    };
    isAnonymous: boolean;
    evaluatorToEvaluateeRole: {
        id: string;
        direction: EvaluatorToEvaluateeRole;
    };
    topicScores: TopicScore[];
    createdAt: Date;
    updatedAt: Date;
    date: number;
    isNoteRequired: boolean;
    updateReason?: string;
};

export type EvaluationCalculate = {
    evaluatorToEvaluateeRole: { direction: EvaluatorToEvaluateeRole };
    topicScores: {
        topic: string;
        topicId: string;
        evaluator: any;
        score: number;
        note?: string;
    }[];
};

export type CombinedEvaluations = EvaluationCalculate[];

export type GroupedScoresByCategory = Partial<Record<string, GroupedScoresByEvaluator>>;

export type GroupedScoresByEvaluator = Partial<Record<EvaluatorToEvaluateeRole, EvaluationScoreValue[]>>;

export type AveragedScoresByCategory = Partial<Record<string, AveragedScoresByEvaluator>>;

export type AveragedScoresByEvaluator = Partial<Record<EvaluatorToEvaluateeRole, number>>;

export type WeightedScoresByEvaluator = Partial<Record<EvaluatorToEvaluateeRole, number>>;

export type CategoryBreakdown = Partial<Record<string, CategoryScoreDetails>>;

export type HolisticScore = {
    technicalScore: DetailedScore;
    culturalScore: DetailedScore;
};

export type DetailedScore = {
    finalScore: number;
    categoryBreakdown: Partial<Record<string, CategoryScoreDetails>>;
};

export type CategoryScoreDetails = {
    finalScore: number;
    weightedEvaluatorScores: WeightedScoresByEvaluator;
    categoryWeight: number;
};
